// IM-XPR-003 (IM-UI-099, IM-UX-115) — Acompanhar: o que mudou, por que importa e o que fazer, num só lugar. Junta, só
// lendo as rotas donas: mudanças em páginas dos concorrentes (monitoramento do battlecard), sinais verificáveis novos
// (concorrentes, empresas-alvo e mercado) e fontes com problema. Organiza por data, impacto, urgência ou empresa e
// filtra por assunto. Sem eventos, oferece ações úteis (ativar páginas recomendadas, revisar fontes, escolher empresas)
// em vez de contadores zerados.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SignalDto, SourceDto } from '@intelimove/contracts';
import { AsyncView } from '../components/States';
import { CATEGORY_LABEL, relativeTime, signalsAdapter } from '../data/adapters';
import { intelApi } from '../data/intel';
import { sourceRecoApi } from '../data/sources';
import { useResource } from '../hooks/useResource';
import { loadCompetitorFacts, loadCompetitorWatch, loadPageRecommendations, plural, type CompetitorWatch, type PagesData } from './data';
import { RecommendedPages } from './RecommendedPages';

export type Subject = 'CONCORRENTE' | 'EMPRESA_ALVO' | 'MERCADO' | 'FONTE';
export type Impact = 'ALTO' | 'MEDIO' | 'BAIXO';
export interface FollowEvent {
  id: string; subject: Subject; company: string; title: string; why: string; action: string; impact: Impact;
  /** 0 = agir agora, 1 = acompanhar de perto, 2 = sem pressa. */
  urgency: 0 | 1 | 2; at: string; to: string; actionLabel: string;
}
export const SUBJECT_LABEL: Record<Subject, string> = { CONCORRENTE: 'Concorrentes', EMPRESA_ALVO: 'Empresas-alvo', MERCADO: 'Mercado', FONTE: 'Fontes' };
const IMPACT_LABEL: Record<Impact, string> = { ALTO: 'Impacto alto', MEDIO: 'Impacto médio', BAIXO: 'Impacto baixo' };
const URGENCY_LABEL = ['Agir agora', 'Acompanhar de perto', 'Sem pressa'] as const;
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Monta os eventos a partir das leituras dos módulos donos (puro). */
export function buildEvents(x: { watch: CompetitorWatch[]; signals: SignalDto[]; problems: SourceDto[]; competitorNames: string[]; targetNames: string[] }): FollowEvent[] {
  const comps = new Set(x.competitorNames.map(norm));
  const targets = new Set(x.targetNames.map(norm));
  const seenSignals = new Set<string>();
  const out: FollowEvent[] = [];
  for (const w of x.watch) {
    for (const c of w.monitoring.changes) {
      if (c.signalId) seenSignals.add(c.signalId);
      out.push({ id: `pc-${c.id}`, subject: 'CONCORRENTE', company: w.competitorName, title: c.title, why: c.explanation ?? `Mudança em ${c.sourceName}.`,
        action: c.impact ?? 'Confira se a mudança pede ajuste no seu posicionamento.', impact: c.relevant ? 'ALTO' : 'BAIXO', urgency: c.relevant ? 1 : 2, at: c.at,
        to: `/battlecards/${w.battlecardId}?aba=monitoramento`, actionLabel: 'Ver no dossiê' });
    }
  }
  for (const s of x.signals) {
    if (seenSignals.has(s.id) || ['REJEITADO', 'SUPERADO'].includes(s.status)) continue;
    const name = norm(s.accountName);
    const subject: Subject = comps.has(name) ? 'CONCORRENTE' : targets.has(name) ? 'EMPRESA_ALVO' : 'MERCADO';
    const timing = s.reading?.timing ?? null;
    out.push({ id: `sg-${s.id}`, subject, company: s.accountName, title: s.title, why: s.reading?.commercialReason ?? `${CATEGORY_LABEL[s.category] ?? 'Sinal'} publicado por ${s.sourceName}.`,
      action: timing === 'ABORDAR_AGORA' ? 'Vale abordar agora.' : subject === 'CONCORRENTE' ? 'Avalie se pede resposta ao concorrente.' : 'Avalie e decida se acompanha.',
      impact: s.score >= 85 ? 'ALTO' : s.score >= 60 ? 'MEDIO' : 'BAIXO', urgency: timing === 'ABORDAR_AGORA' ? 0 : timing === 'ACOMPANHAR_DE_PERTO' ? 1 : 2, at: s.detectedAt,
      to: `/sinais?sinal=${s.id}`, actionLabel: 'Avaliar o sinal' });
  }
  for (const f of x.problems) {
    out.push({ id: `src-${f.id}`, subject: 'FONTE', company: f.accountName ?? f.name, title: `${f.name}: ${f.requiresManualAction ? 'precisa de uma ação sua' : 'a coleta falhou'}`, why: f.lastError ?? 'A última coleta não terminou.',
      action: 'Confira o endereço ou retome a coleta.', impact: 'MEDIO', urgency: 1, at: f.lastRunAt ?? f.createdAt, to: '/fontes?visao=problemas', actionLabel: 'Revisar a fonte' });
  }
  return out;
}

type Order = 'data' | 'impacto' | 'urgencia' | 'empresa';
const ORDERS: Array<[Order, string]> = [['data', 'Data'], ['impacto', 'Impacto'], ['urgencia', 'Urgência'], ['empresa', 'Empresa']];
const RANK: Record<Impact, number> = { ALTO: 0, MEDIO: 1, BAIXO: 2 };
export function sortEvents(xs: readonly FollowEvent[], order: Order): FollowEvent[] {
  const byDate = (a: FollowEvent, b: FollowEvent) => b.at.localeCompare(a.at);
  return [...xs].sort((a, b) => order === 'impacto' ? RANK[a.impact] - RANK[b.impact] || byDate(a, b)
    : order === 'urgencia' ? a.urgency - b.urgency || RANK[a.impact] - RANK[b.impact] || byDate(a, b)
    : order === 'empresa' ? a.company.localeCompare(b.company, 'pt-BR') || byDate(a, b) : byDate(a, b));
}

type Data = { events: FollowEvent[]; pages: PagesData; activeSources: number; companies: number };
const SHOW = 10;

export function Follow() {
  useEffect(() => { document.title = 'Acompanhar · InteliMove'; }, []);
  const { state, reload, refresh } = useResource(async (s): Promise<Data> => {
    const [w, signals, problems, facts, targets, overview] = await Promise.all([
      loadCompetitorWatch(s),
      signalsAdapter.list({ limit: 50 }, s).then((p) => p.items).catch(() => [] as SignalDto[]),
      sourceRecoApi.monitored('problemas', s).then((x) => x.items).catch(() => [] as SourceDto[]),
      loadCompetitorFacts(s).catch(() => null),
      intelApi.targets({ status: 'ACTIVE', limit: 100 }, s).then((x) => x.items.map((i) => String(i.name))).catch(() => [] as string[]),
      sourceRecoApi.overview(s).catch(() => null),
    ]);
    const pages = await loadPageRecommendations(s, w.watch).catch(() => ({ all: [], monitored: [] }));
    const events = buildEvents({ watch: w.watch, signals, problems, competitorNames: (facts?.items ?? []).flatMap((k) => [k.name, k.tradeName ?? '']).filter(Boolean), targetNames: targets });
    return { events, pages, activeSources: overview?.active ?? 0, companies: w.watch.length };
  }, []);
  return (
    <section aria-labelledby="page-title">
      <header className="xp-head">
        <p className="xp-eyebrow">Acompanhar</p>
        <h1 id="page-title">O que mudou, por que importa e o que fazer</h1>
        <p className="xp-lead">Mudanças em concorrentes, empresas-alvo e mercado, num só lugar. Você não precisa procurar em cada módulo.</p>
      </header>
      <AsyncView state={state} onRetry={reload} empty={null} loading="block">
        {(d) => <FollowView d={d} onChanged={refresh} />}
      </AsyncView>
    </section>
  );
}

function FollowView({ d, onChanged }: { d: Data; onChanged: () => void }) {
  const [order, setOrder] = useState<Order>('data');
  const [subject, setSubject] = useState<Subject | 'TODOS'>('TODOS');
  const [all, setAll] = useState(false);
  const subjects = useMemo(() => (Object.keys(SUBJECT_LABEL) as Subject[]).filter((s) => d.events.some((e) => e.subject === s)), [d.events]);
  const list = sortEvents(d.events.filter((e) => subject === 'TODOS' || e.subject === subject), order);
  if (!d.events.length) {
    return (
      <div className="xp-two">
        <div className="xp-panel">
          <h2>Nada mudou por enquanto</h2>
          <p className="xp-quiet">{d.activeSources ? `O InteliMove está acompanhando ${plural(d.activeSources, 'fonte', 'fontes')}. Quando algo mudar, aparece aqui com a explicação do impacto.` : 'Ainda não há páginas nem fontes sendo acompanhadas. Comece ativando as recomendadas.'}</p>
          <RecommendedPages data={d.pages} onChanged={onChanged} />
        </div>
        <aside className="xp-panel">
          <h2>Outras formas de começar</h2>
          <ul className="plain-list">
            <li><Link to="/fontes?visao=recomendadas">Revisar fontes recomendadas</Link></li>
            <li><Link to="/objetivos/empresas">Escolher empresas para acompanhar</Link></li>
            <li><Link to="/objetivos/concorrentes">Conhecer seus concorrentes</Link></li>
          </ul>
        </aside>
      </div>
    );
  }
  const groups = order === 'empresa' ? [...new Set(list.map((e) => e.company))] : null;
  const shown = all ? list : list.slice(0, SHOW);
  const item = (e: FollowEvent) => (
    <li key={e.id}>
      <span className={`xp-impact${e.impact === 'ALTO' ? ' alto' : ''}`}>{IMPACT_LABEL[e.impact]}</span>
      <div>
        <b>{e.title}</b>
        <span><span className="xp-tagline">{e.company}</span>{SUBJECT_LABEL[e.subject]} · {relativeTime(e.at)} · {URGENCY_LABEL[e.urgency]}</span>
        <span><b>Por que importa:</b> {e.why}</span>
        <span><b>O que fazer:</b> {e.action}</span>
      </div>
      <Link className="small-action" to={e.to} aria-label={`${e.actionLabel}: ${e.title}`}>{e.actionLabel}</Link>
    </li>
  );
  return (
    <div className="xp-two">
      <div>
        <div className="xp-filters" role="group" aria-label="Filtrar por assunto">
          {(['TODOS', ...subjects] as const).map((s) => <button key={s} type="button" aria-pressed={subject === s} onClick={() => setSubject(s)}>{s === 'TODOS' ? 'Tudo' : SUBJECT_LABEL[s]}</button>)}
        </div>
        <div className="xp-filters" role="group" aria-label="Organizar por">
          {ORDERS.map(([o, l]) => <button key={o} type="button" aria-pressed={order === o} onClick={() => setOrder(o)}>{`Por ${l.toLowerCase()}`}</button>)}
        </div>
        <h2 className="sr-only">Mudanças</h2>
        {groups ? groups.map((g) => {
          const xs = shown.filter((e) => e.company === g);
          return xs.length ? <section key={g} aria-label={g}><h3 className="xp-kicker">{g}</h3><ul className="xp-feed" role="list">{xs.map(item)}</ul></section> : null;
        }) : <ul className="xp-feed" role="list">{shown.map(item)}</ul>}
        {list.length > SHOW && <button type="button" className="link-btn" onClick={() => setAll((a) => !a)} aria-expanded={all}>{all ? 'Mostrar menos' : `Ver todas (${list.length})`}</button>}
      </div>
      <aside className="xp-panel">
        <span className="xp-kicker">Acompanhamento</span>
        <h2>O que está sendo acompanhado</h2>
        <p className="xp-quiet">{d.activeSources ? `${plural(d.activeSources, 'fonte ativa', 'fontes ativas')}` : 'Nenhuma fonte ativa'}{d.companies ? ` · ${plural(d.companies, 'concorrente com dossiê', 'concorrentes com dossiê')}` : ''}.</p>
        <RecommendedPages data={d.pages} onChanged={onChanged} />
      </aside>
    </div>
  );
}
